import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  createChannelPartnerLead,
  getChannelPartner,
  listChannelPartnerLeads,
  updateChannelPartner,
} from "../../services/channelPartnerService";
import { listTenantOrganizations } from "../../services/organizationService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import { getProjectDetails } from "../Project/components/ShowAll/ShowAllService";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import urls from "../../global/constants/UrlConstants";
import YhButton from "../../ui/YhButton";
import YhSelect, { buildStageSelectGroups } from "../../ui/YhSelect";
import YhInput from "../../ui/YhInput";
import YhMultiSelect from "../../ui/YhMultiSelect";
import YhModal from "../../ui/YhModal";
import PhoneWithCountryCode, {
  composePhoneWithCountry,
  defaultPhoneCountry,
  findPhoneCountry,
  formatPhoneKeepingCountryCode,
  nationalPhoneDigits,
  resolveLeadPhoneForDisplay,
} from "../../Shared/Phone/PhoneWithCountryCode";

const emptyLeadForm = () => ({
  leadName: "",
  leadPhone: "",
  phoneCountryCode: defaultPhoneCountry().code,
  leadEmail: "",
  organizationIds: [] as string[],
  projectIds: [] as string[],
  stage: "",
});

function firstStageValue(
  groups: { options: { value: string | number }[] }[]
) {
  for (const g of groups || []) {
    if (g?.options?.[0]?.value != null && g.options[0].value !== "") {
      return String(g.options[0].value);
    }
  }
  return "Lead";
}

const ChannelPartnerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<any>(null);
  const [leads, setLeads] = useState<any[]>([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [orgFilter, setOrgFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [projectFilter] = useState("");
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [leadForm, setLeadForm] = useState(emptyLeadForm());
  const [projects, setProjects] = useState<any[]>([]);
  const [stageData, setStageData] = useState<any[]>([]);

  const stageGroups = useMemo(
    () => buildStageSelectGroups(stageData || []),
    [stageData]
  );

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [d, leadsRes] = await Promise.all([
        getChannelPartner(id),
        listChannelPartnerLeads(id, {
          page: 1,
          limit: 100,
          organizationId: orgFilter || undefined,
          projectId: projectFilter || undefined,
          stage: stageFilter || undefined,
        }),
      ]);
      if (d?.success === 0) {
        openErrorNotification(d?.message || "Failed to load Channel Partner");
        setDetail(null);
        setLeads([]);
        setLeadsTotal(0);
        return;
      }
      setDetail(d?.data || d);
      if (leadsRes?.success === 0) {
        openErrorNotification(leadsRes?.message || "Failed to load leads");
        setLeads([]);
        setLeadsTotal(0);
      } else {
        const ld = leadsRes?.data || leadsRes;
        setLeads(ld?.items || []);
        setLeadsTotal(Number(ld?.total) || 0);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load Channel Partner");
    } finally {
      setLoading(false);
    }
  }, [id, orgFilter, projectFilter, stageFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await listTenantOrganizations();
        const list = res?.data?.items || res?.data || res?.items || [];
        const map: Record<string, string> = {};
        (Array.isArray(list) ? list : []).forEach((o: any) => {
          map[String(o._id)] = o.brandName || o.name || String(o._id);
        });
        setOrgNames(map);
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getProjectDetails({
          pageNumber: 0,
          pageSize: 500,
          search: "",
          status: "1",
          propertyPurpose: "",
        });
        setProjects(Array.isArray(res?.data) ? res.data : []);
      } catch (_) {
        setProjects([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getStage();
        setStageData(Array.isArray(res?.data) ? res.data : []);
      } catch (_) {
        setStageData([]);
      }
    })();
  }, []);

  const stageBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of leads) {
      const s = String(l.stage || "Unknown");
      map.set(s, (map.get(s) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [leads]);

  const orgBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of leads) {
      const oid = String(l.organizationId || "—");
      map.set(oid, (map.get(oid) || 0) + 1);
    }
    return Array.from(map.entries());
  }, [leads]);

  const membershipOrgOptions = useMemo(() => {
    const mems = detail?.memberships || [];
    return [
      { label: "All organizations", value: "" },
      ...mems.map((m: any) => ({
        label: m.organizationName || String(m.organizationId),
        value: String(m.organizationId),
      })),
    ];
  }, [detail]);

  const addLeadOrgOptions = useMemo(() => {
    const mems = detail?.memberships || [];
    return mems.map((m: any) => ({
      label:
        m.organizationName ||
        orgNames[String(m.organizationId)] ||
        String(m.organizationId),
      value: String(m.organizationId),
    }));
  }, [detail, orgNames]);

  const leadSelectedOrgIds = useMemo(
    () => leadForm.organizationIds || [],
    [leadForm.organizationIds]
  );

  const leadProjectOptions = useMemo(() => {
    const orgSet = new Set(leadSelectedOrgIds.map(String).filter(Boolean));
    const mems = detail?.memberships || [];
    return (projects || [])
      .filter((p: any) => {
        if (!orgSet.size) return false;
        const oid = String(p.organizationId || "");
        if (oid && !orgSet.has(oid)) return false;
        if (!mems.length) return true;
        const mem = mems.find((m: any) => String(m.organizationId) === oid);
        if (!mem) return !oid;
        const pids = Array.isArray(mem.projectIds) ? mem.projectIds : [];
        if (!pids.length) return true;
        return pids.map(String).includes(String(p._id));
      })
      .map((p: any) => ({
        label: p.project_name || p.name || p.project_uid || String(p._id),
        value: String(p._id),
      }));
  }, [projects, leadSelectedOrgIds, detail]);

  useEffect(() => {
    const allowed = new Set(leadProjectOptions.map((p) => String(p.value)));
    setLeadForm((f) => {
      const next = (f.projectIds || []).filter((id) => allowed.has(String(id)));
      if (next.length === (f.projectIds || []).length) return f;
      return { ...f, projectIds: next };
    });
  }, [leadProjectOptions]);

  const openAddLead = () => {
    const mems = detail?.memberships || [];
    const defaultOrgs = orgFilter
      ? [orgFilter]
      : mems.length === 1
        ? [String(mems[0].organizationId)]
        : [];
    setLeadForm({
      ...emptyLeadForm(),
      organizationIds: defaultOrgs,
      projectIds: [],
      stage: stageFilter || firstStageValue(stageGroups),
    });
    setAddOpen(true);
  };

  const submitLead = async () => {
    if (!id) return;
    if (!leadForm.leadName.trim()) {
      openErrorNotification("Lead name is required");
      return;
    }
    const national = nationalPhoneDigits(leadForm.leadPhone, 15);
    if (
      !(
        national.length === 4 ||
        (national.length >= 7 && national.length <= 15)
      )
    ) {
      openErrorNotification(
        "Enter a valid mobile (10 digits) or last 4 digits"
      );
      return;
    }
    if (!leadForm.stage) {
      openErrorNotification("Select a stage");
      return;
    }
    const organizationIds = leadForm.organizationIds || [];
    if (!organizationIds.length) {
      openErrorNotification("Select at least one organization");
      return;
    }
    const country =
      findPhoneCountry(leadForm.phoneCountryCode) || defaultPhoneCountry();
    const composed = composePhoneWithCountry(country.dialCode, national);
    setSavingLead(true);
    try {
      const res = await createChannelPartnerLead(id, {
        leadName: leadForm.leadName.trim(),
        leadPhone: composed,
        phoneDialCode: country.dialCode,
        phoneCountryCode: country.code,
        leadEmail: leadForm.leadEmail.trim() || undefined,
        organizationIds,
        projectIds: leadForm.projectIds || [],
        stage: leadForm.stage,
      });
      if (res?.success === 0 || !res?.data) {
        openErrorNotification(res?.message || "Failed to add lead");
        return;
      }
      const referenced = Number(res?.data?.referencedCount || 0);
      const count = Number(res?.data?.createdCount || 1);
      openSuccessNotification(
        referenced > 0 && referenced >= count
          ? "Existing lead linked to this Channel Partner (reference)."
          : referenced > 0
            ? `Lead saved — ${referenced} existing lead(s) linked as reference.`
            : count > 1
              ? `Lead added for ${count} organizations.`
              : "Lead added for Channel Partner."
      );
      if (res?.data?.errors?.length) {
        openErrorNotification(res.data.errors.join("; "));
      }
      // Clear filters that would hide the newly created lead
      const createdOrgIds = (
        Array.isArray(res.data?.leads) ? res.data.leads : [res.data]
      )
        .map((l: any) => String(l?.organizationId || ""))
        .filter(Boolean);
      if (orgFilter && !createdOrgIds.includes(String(orgFilter))) {
        setOrgFilter("");
      }
      if (stageFilter && stageFilter !== leadForm.stage) {
        setStageFilter("");
      }
      setAddOpen(false);
      setLeadForm(emptyLeadForm());
      await load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to add lead");
    } finally {
      setSavingLead(false);
    }
  };

  const toggleMasking = async () => {
    if (!detail?._id) return;
    try {
      await updateChannelPartner(detail._id, {
        phoneMaskingEnabled: !detail.phoneMaskingEnabled,
      });
      openSuccessNotification("Masking updated");
      load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Update failed");
    }
  };

  if (loading && !detail) {
    return (
      <div className="px-6 py-8 text-sm text-[#6B7280]">Loading partner…</div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-3 px-6 py-8">
        <p className="text-sm text-[#6B7280]">Channel Partner not found.</p>
        <YhButton
          variant="secondary"
          size="sm"
          onClick={() => history.push(urls.channelPartnersPath)}
        >
          Back to list
        </YhButton>
      </div>
    );
  }

  return (
    <div className="box-border h-full min-h-0 w-full space-y-5 overflow-auto px-4 py-4 md:px-6 md:py-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            className="mb-2 text-sm font-medium text-[#A02448] hover:underline"
            onClick={() => history.push(urls.channelPartnersPath)}
          >
            ← All Channel Partners
          </button>
          <h1 className="text-2xl font-semibold text-[#111827]">
            {detail.firmName}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {formatPhoneKeepingCountryCode(detail.phone)}
            {detail.email ? ` · ${detail.email}` : ""}
            {detail.grade ? ` · ${detail.grade}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <YhButton variant="primary" size="sm" onClick={openAddLead}>
            Add lead
          </YhButton>
          <YhButton variant="secondary" size="sm" onClick={toggleMasking}>
            Masking: {detail.phoneMaskingEnabled ? "On" : "Off"}
          </YhButton>
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => history.push(urls.cpBulkUploadPath)}
          >
            CP Bulk Upload
          </YhButton>
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => history.push(urls.channelPartnersPath)}
          >
            All Channel Partners
          </YhButton>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <DashCard label="Total leads" value={detail.leadsCount ?? leadsTotal} />
        <DashCard
          label="Organizations"
          value={(detail.memberships || []).length}
        />
        <DashCard
          label="Projects linked"
          value={(detail.memberships || []).reduce(
            (acc: number, m: any) =>
              acc + (Array.isArray(m.projectIds) ? m.projectIds.length : 0),
            0
          )}
        />
        <DashCard label="Status" value={detail.status || "—"} isText />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-[#111827]">
            Leads by stage
          </h3>
          {stageBreakdown.length === 0 ? (
            <p className="text-sm text-[#9CA3AF]">No lead data yet.</p>
          ) : (
            <ul className="space-y-2">
              {stageBreakdown.map(([stage, count]) => (
                <li
                  key={stage}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-[#374151]">{stage}</span>
                  <span className="font-semibold text-[#111827]">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-[#111827]">
            Memberships
          </h3>
          <ul className="space-y-2 text-sm text-[#374151]">
            {(detail.memberships || []).length === 0 ? (
              <li className="text-[#9CA3AF]">No org memberships.</li>
            ) : (
              (detail.memberships || []).map((m: any) => (
                <li key={String(m._id)}>
                  <span className="font-medium">
                    {m.organizationName || m.organizationId}
                  </span>
                  <span className="text-[#9CA3AF]">
                    {Array.isArray(m.projectIds) && m.projectIds.length
                      ? ` · ${m.projectIds.length} project(s)`
                      : " · all projects"}
                  </span>
                </li>
              ))
            )}
          </ul>
          {orgBreakdown.length > 0 && (
            <div className="mt-4 border-t border-[#F3F4F6] pt-3">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
                Leads by org (current page)
              </h4>
              <ul className="space-y-1 text-sm">
                {orgBreakdown.map(([oid, count]) => (
                  <li key={oid} className="flex justify-between">
                    <span>{orgNames[oid] || oid}</span>
                    <span className="font-semibold">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-[#E5E7EB] bg-white p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-[#111827]">
              Leads ({leadsTotal})
            </h2>
            <p className="mt-0.5 text-xs text-[#6B7280]">
              Phone numbers always show last 4 digits only for Channel Partner
              views.
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-3 sm:w-auto sm:grid-cols-2 lg:grid-cols-4">
            <YhSelect
              label="Filter organization"
              value={orgFilter}
              onChange={(e: any) =>
                setOrgFilter(String(e?.target?.value || ""))
              }
              options={membershipOrgOptions}
              searchable
              searchPlaceholder="Search organizations..."
              showOptionIcon={false}
            />
            <YhSelect
              label="Filter stage"
              placeholder="All stages"
              searchPlaceholder="Search stages..."
              searchable
              clearable
              showOptionIcon={false}
              value={stageFilter}
              groups={stageGroups}
              onChange={(e: any) =>
                setStageFilter(String(e?.target?.value || ""))
              }
            />
            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-2">
              <YhButton variant="secondary" size="sm" onClick={load}>
                Refresh
              </YhButton>
              <YhButton variant="primary" size="sm" onClick={openAddLead}>
                Add lead
              </YhButton>
            </div>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="bg-[#F9FAFB] text-[#6B7280]">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Phone (last 4)</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Stage</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Project</th>
                <th className="px-3 py-2 font-medium">Organization</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-[#6B7280]" colSpan={7}>
                    No leads linked to this Channel Partner yet. Use{" "}
                    <strong>Add lead</strong> to capture one.
                  </td>
                </tr>
              ) : (
                leads.map((l) => (
                  <tr key={l._id} className="border-t border-[#F3F4F6]">
                    <td className="px-3 py-2 font-medium text-[#111827]">
                      {l.leadName || "—"}
                    </td>
                    <td className="px-3 py-2 font-mono tracking-wide">
                      {formatPhoneKeepingCountryCode(
                        resolveLeadPhoneForDisplay(l)
                      )}
                    </td>
                    <td className="px-3 py-2">{l.leadEmail || "—"}</td>
                    <td className="px-3 py-2">{l.stage || "—"}</td>
                    <td className="px-3 py-2">{l.source || "—"}</td>
                    <td className="px-3 py-2">{l.projectId || "—"}</td>
                    <td className="px-3 py-2 font-medium text-[#111827]">
                      {orgNames[String(l.organizationId)] ||
                        String(l.organizationId || "—")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <YhModal
        open={addOpen}
        onClose={() => !savingLead && setAddOpen(false)}
        title={`Add lead · ${detail.firmName}`}
        width={520}
        footer={
          <>
            <YhButton
              variant="secondary"
              fullWidth={false}
              disabled={savingLead}
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              fullWidth={false}
              loading={savingLead}
              onClick={submitLead}
            >
              Save lead
            </YhButton>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-[#6B7280]">
            This lead is tracked to <strong>{detail.firmName}</strong>. Enter
            mobile with country code (full 10 digits or last 4). Source is set
            to Channel Partner automatically.
          </p>
          <YhMultiSelect
            label="Organizations"
            placeholder="Select organizations…"
            searchPlaceholder="Search organizations…"
            requiredMark
            showSelectAll
            maxVisibleChips={3}
            options={addLeadOrgOptions}
            value={leadForm.organizationIds}
            onChange={(values) =>
              setLeadForm((f) => ({
                ...f,
                organizationIds: values.map(String),
                projectIds: [],
              }))
            }
          />
          <YhMultiSelect
            label="Projects"
            placeholder={
              leadSelectedOrgIds.length
                ? "Select projects…"
                : "Select organization first…"
            }
            searchPlaceholder="Search projects…"
            showSelectAll
            maxVisibleChips={3}
            disabled={!leadSelectedOrgIds.length}
            options={leadProjectOptions}
            value={leadForm.projectIds}
            onChange={(values) =>
              setLeadForm((f) => ({
                ...f,
                projectIds: values.map(String),
              }))
            }
          />
          <YhInput
            label="Lead name *"
            value={leadForm.leadName}
            onChange={(e: any) =>
              setLeadForm((f) => ({ ...f, leadName: e.target.value }))
            }
            placeholder="Customer name"
          />
          <PhoneWithCountryCode
            label="Mobile"
            required
            countryCode={leadForm.phoneCountryCode}
            phone={leadForm.leadPhone}
            phonePlaceholder="10-digit or last 4"
            onCountryChange={(code) =>
              setLeadForm((f) => ({ ...f, phoneCountryCode: code }))
            }
            onPhoneChange={(digits) =>
              setLeadForm((f) => ({ ...f, leadPhone: digits }))
            }
          />
          <YhInput
            label="Email"
            value={leadForm.leadEmail}
            onChange={(e: any) =>
              setLeadForm((f) => ({ ...f, leadEmail: e.target.value }))
            }
            placeholder="optional"
          />
          <YhSelect
            label="Stage *"
            placeholder="Select stage"
            searchPlaceholder="Search stages..."
            searchable
            showOptionIcon={false}
            value={leadForm.stage}
            groups={stageGroups}
            onChange={(e: any) =>
              setLeadForm((f) => ({
                ...f,
                stage: String(e?.target?.value || ""),
              }))
            }
          />
        </div>
      </YhModal>
    </div>
  );
};

function DashCard({
  label,
  value,
  isText,
}: {
  label: string;
  value: any;
  isText?: boolean;
}) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-[#9CA3AF]">
        {label}
      </div>
      <div
        className={`mt-1 font-semibold text-[#111827] ${
          isText ? "text-base capitalize" : "text-2xl"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export default ChannelPartnerDetailPage;
